import { Platform } from 'react-native';
import { uploadToR2 } from '../services/r2Storage';
import { compressImage, dataUrlToBlob } from './imageCompression';

type ProgressCallback = (progress: { loaded: number; total: number; percentage: number }) => void;

/**
 * Upload an image to R2 storage
 * Compresses the image before upload
 */
export async function uploadImage(
  localUri: string,
  onProgress?: ProgressCallback
): Promise<string> {
  // Compress the image (resize + convert to WebP on web, JPEG on native)
  const compressed = await compressImage(localUri);

  let blob: Blob;

  if (Platform.OS === 'web' && compressed.uri.startsWith('data:')) {
    // Web: Convert data URL to Blob
    blob = dataUrlToBlob(compressed.uri);
  } else {
    // Native: Fetch the compressed image URI
    const response = await fetch(compressed.uri);
    blob = await response.blob();
  }

  // Determine file extension based on mime type
  const fileExtension = compressed.mimeType === 'image/webp' ? 'webp' : 'jpg';

  // Create a File object with the proper name and type
  const file = new File([blob], `image.${fileExtension}`, { type: compressed.mimeType });

  // Upload to R2
  const url = await uploadToR2(file, 'images', onProgress);
  return url;
}

/**
 * Upload multiple images to R2 storage
 * Returns array of URLs in the same order as input
 */
export async function uploadImages(
  localUris: string[],
  onProgress?: (overall: { current: number; total: number; percentage: number }) => void
): Promise<string[]> {
  const urls: string[] = [];
  const total = localUris.length;

  for (let i = 0; i < localUris.length; i++) {
    const url = await uploadImage(localUris[i]);
    urls.push(url);

    onProgress?.({
      current: i + 1,
      total,
      percentage: Math.round(((i + 1) / total) * 100),
    });
  }

  return urls;
}
